import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TabStripLowDom from '@components-clean/core/tabstrip-low-dom';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { IFormData, RiskMonitoringContext } from '@pages-clean/risk-assessment/risk-monitoring/interfaces/context';
import ManageActions from '@pages-clean/risk-assessment/risk-monitoring/manage-actions';
import ManageObservations from '@pages-clean/risk-assessment/risk-monitoring/manage-observations';
import { NullableStudy } from '@contexts/local-state-context';
import { LoggedInPageProps } from '@config/routes/interfaces';

interface ICTQFactorsWrapperProps {
    study: NullableStudy | undefined;
}

const Content = ({ study }: ICTQFactorsWrapperProps) => {
    const { confirmDiscardChanges, anyComponentHasChanges, discardChanges } = useUnsavedChangesContext();
    const { t } = useTranslation('ram', { keyPrefix: 'riskMonitoring' });

    const [formData, setFormData] = useState<IFormData>({
        study,
        showDeleted: false,
        selectedObservation: undefined,
        selectedObservationRisk: undefined,
        actionsGridFiltersResetToken: 0,
        actionsGridIsFiltered: false,
        selectedAction: undefined,
        observationCount: 0,
        actionCount: 0,
    });
    const riskLogProviderValue = useMemo(
        () => ({
            current: formData,
            setCurrent: setFormData,
        }),
        [formData, setFormData],
    );

    useEffect(() => {
        setFormData({
            ...formData,
            study,
        });
    }, [study?.id]);

    return (
        <div style={{ height: '100%' }}>
            <RiskMonitoringContext.Provider value={riskLogProviderValue}>
                <TabStripLowDom
                    data={[
                        {
                            title: t('tabs.manageObservations'),
                            content: <ManageObservations />,
                            dataTestId: 'rm-manage-observations-tab',
                        },
                        {
                            title: t('tabs.manageActions'),
                            content: <ManageActions />,
                            dataTestId: 'rm-manage-actions-tab',
                        },
                    ]}
                    canChangeTab={async () => {
                        if (anyComponentHasChanges()) {
                            const confirmed = await confirmDiscardChanges();
                            if (!confirmed) {
                                return false;
                            }
                            discardChanges();
                        }
                        return true;
                    }}
                />
            </RiskMonitoringContext.Provider>
        </div>
    );
};

const RiskMonitoringWrapper = ({ study }: ICTQFactorsWrapperProps) => {
    const content = <Content study={study} />;
    return <>{content}</>;
};

const RiskMonitoring = ({ study }: LoggedInPageProps) => <RiskMonitoringWrapper study={study} />;
export default RiskMonitoring;
